// Order models - converted from ActionScript remote.order.*

import { ProjectInfo } from './app.model';

/**
 * OrderInfo - from remote/order/OrderInfo.as
 * Main order container sent to OrderService.addToCart()
 */
export interface OrderInfo {
  _explicitType?: 'remote.order.OrderInfo';
  orderId: number;
  code: string | null;
  orderDate: string | null;
  urgent: number;
  status: number;
  groups: OrderItemsGroupInfo[];
  vouchers: any[];
  payment: PaymentInfo | null;
  currencyId: number;
  totalValue: number;
  baseValue: number;
  vatValue: number;
  showVat: number;
  recargoValue: number;
  vouchersTotalDiscount: number;
  customerTotalDiscount: number;
  totalDiscount: number;
  balanceId: number;
  discountedBalance: number;
  orderTotals: any[] | null;
  shipmentType: ShipmentTypeInfo | null;
  shipmentMethod: ShipmentMethodInfo | null;
  shipmentAddress: any | null;
  shipmentTrackings: any[] | null;
  billingAddress: any | null;
  totalShippingPrice: number;
  extras: any[];
  warning: string;
  issues: any[];
  allowUrgent: number;
  version: number;
  estimatedDeliveryDate: string | null;
  /** Plafond orders (pre-paid purchase / extension). The backend's
   * getShipments reads this flag off the posted payload
   * (checkIsPlafondOrderFromData) and returns the no-shipping shipment
   * types. Plafond orders are standalone — never mixed with other
   * products — and go through the direct-checkout dialog, not the cart. */
  plafond?: boolean;
}

/**
 * OrderItemsGroupInfo - from remote/order/OrderItemsGroupInfo.as
 * A group of order items (products)
 */
export interface OrderItemsGroupInfo {
  _explicitType?: 'remote.order.OrderItemsGroupInfo';
  id: number;
  orderId: number;
  code: string | null;
  status: number;
  products: OrderItemInfo[];
  pack: boolean;
  name: string | null;
  showcase: number;
  showcaseEnabled: number;
  showcaseStatus: number;
  observations: string;
  price: number;
  showPrices: boolean;
  extraValues: any[] | null;
  extraValueUnique: number;
  backup: BackupInfo | null;
  draftId: number;
  unwantedProductTypes: any[];
  estimatedDeliveryDate: string | null;
}

// Status constants for OrderItemsGroupInfo
export const OrderItemsGroupStatus = {
  PENDING: 1,
  DOWNLOADING: 2,
  DOWNLOADED: 3,
  BUILDING_PDF: 4,
  COMPLETE: 5
} as const;

/**
 * OrderItemInfo - from remote/order/OrderItemInfo.as
 * Individual product item in an order
 */
export interface OrderItemInfo {
  _explicitType?: 'remote.order.OrderItemInfo';
  id: number;
  processingStatus: number;
  idName: string | null;
  code: string | null;
  refPHC: string;
  subCategoryName: string | null;
  productTypeId: number;
  productSubTypeId: number;
  productId: number;
  productName: string;
  sectorId: number;
  finishes: OrderItemFinishInfo[];
  finishName: string | null;
  name: string;
  numPages: number;
  numExtraPages: number;
  margins: number;
  qty: number;
  qtyName: string | null;
  width: number;
  height: number;
  type: number;
  projectInfo: ProjectInfo | null;
  files: OrderItemFileInfo[];
  showPrice: boolean;
  prices: AMFProductPriceInfo[] | null;
  price: number;
  priceName: string | null;
  stock: AMFProductStockInfo | null;
  color: number;
  fontSize: number;
  strokeColor: number;
  showVouchers: boolean;
  includeInOrder: boolean;
  showcase?: number;           // 1 = mostruário, 0 = normal
  showcaseEnabled?: number;    // 1 = showcase option available for this product
  extraValues?: any[];         // Extra price values (urgency, etc)
  /** Plafond-extension lines only: the voucherId of the plafond being
   * extended. The backend reads it off the item at payment confirmation and
   * pushes that plafond's expiry out — without it the line is charged and
   * skipped. Passed through cleanOrderPayload untouched. */
  extendedVoucherId?: string;
}

// Type constants for OrderItemInfo
export const OrderItemType = {
  NORMAL: 0,
  PROJECT: 1
} as const;

/**
 * OrderItemFileInfo - from remote/order/OrderItemFileInfo.as
 * File attached to an order item (extends ImageInfo)
 */
export interface OrderItemFileInfo {
  _explicitType?: 'remote.order.OrderItemFileInfo';
  // From ImageInfo base
  id: number;
  name: string | null;
  serverName: string | null;
  serverId: number;
  pathStructureId: number;
  width: number;
  height: number;
  size: number;
  md5?: string | null;
  fileTypeId?: number;
  // OrderItemFileInfo specific
  qty: number;
  thumbBytes: ArrayBuffer | null;
  finishOptionId: number;
  editorViewIndex: number;
  isProcessedFile: boolean;
  processedFileUrl: string | null;
  deleted: boolean;
}

/**
 * OrderItemFinishInfo - finish option selected for an order item
 */
export interface OrderItemFinishInfo {
  _explicitType?: 'remote.order.OrderItemFinishInfo';
  id: number;
  name: string;
  optionId: number;
  optionName: string;
  optionTypeId: number;
  data: any;
  withoutPrice: number;
  qty: number;
}

/**
 * AMFProductPriceInfo - from remote/product/ProductPriceInfo.as
 * Full price information for AMF communication (different from product.model.ts ProductPriceInfo)
 */
export interface AMFProductPriceInfo {
  _explicitType?: 'remote.product.ProductPriceInfo';
  billingProductId: number;
  ref: string | null;
  price: number;
  extraPagePrice: number;
  discount1: number;
  discount2: number;
  discount3: number;
  discount4: number;
  discount5: number;
  discount6: number;
  discountId: number;
  discountsNotAllowed: number;
  vatTab: number;
  recargoTax: number;
  vat: number;
  incVat: number;
  netPrice: number;
  code: string | null;
  unit: string | null;
  weight: number;
  width: number;
  height: number;
  length: number;
  vouchers: any[] | null;
  voucherDiscountValue: number;
  qty: number;
  unitQty: number;
  availableStock: number;
  orderedStock: number;
  stockOff: number;
}

/**
 * AMFProductStockInfo - stock information for AMF communication
 */
export interface AMFProductStockInfo {
  _explicitType?: 'remote.product.ProductStockInfo';
  available: number;
  ordered: number;
  stockOff: number;
}

/**
 * PaymentInfo - from remote/order/PaymentInfo.as
 */
export interface PaymentInfo {
  _explicitType?: 'remote.order.PaymentInfo';
  id: number;
  type: number;
  status: number;
  value: number;
  ref: string | null;
  date: string | null;
}

/**
 * ShipmentTypeInfo - from remote/order/ShipmentTypeInfo.as
 */
export interface ShipmentTypeInfo {
  _explicitType?: 'remote.order.ShipmentTypeInfo';
  id: number;
  name: string;
  price: number;
}

/**
 * ShipmentMethodInfo - from remote/order/ShipmentMethodInfo.as
 */
export interface ShipmentMethodInfo {
  _explicitType?: 'remote.order.ShipmentMethodInfo';
  id: number;
  name: string;
  price: number;
  deliveryTime: string | null;
}

/**
 * BackupInfo - from remote/order/BackupInfo.as
 */
export interface BackupInfo {
  _explicitType?: 'remote.order.BackupInfo';
  id: number;
  name: string | null;
  status: number;
  url: string | null;
}

/**
 * ShipmentMethodInfo constants
 */
export const ShipmentMethodType = {
  ADDRESSES: 0,
  POINTS: 1,
  STORES: 2
} as const;

/**
 * PaymentType constants - from remote/order/PaymentInfo.as
 */
export const PaymentType = {
  STORE: 1,
  PAYPAL: 2,
  MULTIBANCO: 3,
  BANK_TRANSFER: 4,
  COLLECTION: 5,
  BRAINTREE: 6,
  BPAG: 7,
  BOLETO: 8,
  // Bradesco-issued boleto via the REST payments API (gateway "boleto").
  // Sits beside the legacy BOLETO=8 the same way MULTIBANCO_V2=13 sits
  // beside MULTIBANCO=3.
  BOLETO_V2: 10,
  BR_BANK_TRANSFER: 11,
  CIELO: 12,
  MULTIBANCO_V2: 13,
  VIVA_WALLET: 14
} as const;

/**
 * Order grouping ("Agrupar Envios") — shipments sharing a destination can be
 * grouped to ship together. See GET/POST /api/orders/groups.
 */
export interface OrdersGroup {
  /** PHC group stamp for a saved group, or the literal "TEMPORARY_GROUP". */
  groupId: string;
  /** false = blocked (won't ship grouped). */
  enabled: boolean;
  /** order id strings. */
  orders: string[];
  /** "YYYY-MM-DD" or the literal "UNKNOWN". */
  shippingDate: string;
  /** shipping-address fingerprint — only orders sharing this may be grouped. */
  addressMd5: string;
}

/**
 * Normalized result of GET/POST /api/orders/groups. The wire response is
 * always a JSON object — {groups:[...]} on success or {status:'...'} otherwise
 * (both also carry an ignorable _token) — which OrderService flattens to the
 * group array or a {status} object.
 */
export type OrdersGroupsResponse = OrdersGroup[] | { status: string };

/** POST /api/orders/groups body — the complete desired set. */
export interface SaveOrdersGroupsRequest {
  groups: Array<Pick<OrdersGroup, 'groupId' | 'orders' | 'enabled' | 'addressMd5'>>;
}

export const TEMPORARY_GROUP_ID = 'TEMPORARY_GROUP';

/**
 * OrderExtraType constants - from remote/order/OrderExtraInfo.as
 */
export const OrderExtraType = {
  SHIPMENT: 1,
  PAYMENT: 2
} as const;

/**
 * Full ShipmentMethodInfo - from remote/order/ShipmentMethodInfo.as
 * Returned by OrderService.getShipmentMethods
 */
export interface ShipmentMethodInfoFull {
  _explicitType?: 'remote.order.ShipmentMethodInfo';
  id: number;
  ref: string; // 'ADDRESSES' | 'POINTS' | 'STORES'
  name: string;
  description: string;
}

/**
 * ShipmentTypeInfo - from remote/order/ShipmentTypeInfo.as
 * Returned by OrderService.getShipments
 */
export interface ShipmentTypeInfoFull {
  _explicitType?: 'remote.order.ShipmentTypeInfo';
  shipmentTypeId: string;
  name: string;
  description: string;
  price: string;
  discount: number | null;
  paymentDiscount: number;
  shipments: ShipmentInfoFull[];
  showPrice?: boolean;
  // UI state
  selected?: boolean;
  allowSelect?: boolean;
}

/**
 * ShipmentInfo - from remote/order/ShipmentInfo.as
 * Individual shipment option within a ShipmentType
 *
 * Backend serialization (POST /api/orders/shipments, inner shipments[]):
 *   - price/discount/paymentDiscount/vat/recargoTax are numbers (rounded
 *     to 2dp). No parseFloat wrapping needed.
 *   - incVat is a bool (legacy was int 0/1).
 *   - vatTab is an int.
 *   - shipmentId is the canonical id (legacy field name was shippingId —
 *     gone after the dreambooks-api alignment).
 *   - deliveryDays is the canonical field (legacy was shippingDays — kept
 *     here as optional fallback for any unmigrated response shape).
 */
export interface ShipmentInfoFull {
  _explicitType?: 'remote.order.ShipmentInfo';
  shipmentId: string;
  name: string;
  description: string;
  price: number;
  discount: number;
  paymentDiscount: number;
  incVat: boolean;
  vat: number;
  recargoTax: number;
  vatTab: number;
  packaging: PackagingInfoFull | null;
  packagingId?: number | null;
  carrierRef?: string | null;
  voucher: any | null;
  deliveryDays?: number;
  /** @deprecated use {@link deliveryDays} — kept as fallback for legacy responses */
  shippingDays?: number;
}

/**
 * PackagingInfo - from remote/order/PackagingInfo.as
 */
export interface PackagingInfoFull {
  _explicitType?: 'remote.order.PackagingInfo';
  id: string;
  name: string;
  width: string;
  length: string;
  height: string;
  weight: string;
  innerWidth: string;
  innerLength: string;
  innerHeight: string;
  maxWeight: string;
  packedItems: PackedItemInfo[];
  packedItemsWeight: number;
  dynamicSize?: string;
}

/**
 * PackedItemInfo - item packed in packaging
 */
export interface PackedItemInfo {
  name: string;
  width: number;
  length: number;
  height: number;
  weight: number;
}

/**
 * OrderExtraInfo - from remote/order/OrderExtraInfo.as
 * Returned by OrderService.getExtras
 */
export interface OrderExtraInfoFull {
  _explicitType?: 'remote.order.OrderExtraInfo';
  id: string;
  name: string;
  description: string;
  priceInfo: AMFProductPriceInfo;
  price: number;
  discount: number | null;
  type: number | null;
  showPrice?: boolean;
  // UI state
  selected?: boolean;
}

/**
 * PaymentDiscountInfo - the discount RULE attached to a payment method (as
 * returned by getPayments). For a percentage discount, `percValue` carries the
 * percent (e.g. 3 = 3%) and there is no `price` until the backend applies it to
 * the order total. `type` is the discount type (3 = percentage).
 */
export interface PaymentDiscountInfo {
  extraServiceId?: number;
  type?: number;
  billingProductId?: number;
  ref?: string;
  percValue?: number;
  hasCondition?: boolean;
  condition?: string;
  name?: string;
  // Present only once the backend has computed the monetary value.
  price?: number;
}

/**
 * PaymentInfoFull - from remote/order/PaymentInfo.as
 * Returned by OrderService.getPayments
 */
export interface PaymentInfoFull {
  _explicitType?: 'remote.order.PaymentInfo';
  id: string;
  name: string;
  description: string;
  image: string;
  type: string;
  extraTax: OrderExtraInfoFull | null;
  discount: PaymentDiscountInfo | null;
  // UI state
  selected?: boolean;
  allowSelect?: boolean;
}

/**
 * PointShipmentAddress - address format for pickup points
 */
export interface PointShipmentAddress {
  _explicitType?: 'remote.common.PointShipmentAddress';
  pointId: number | string;
  pointName: string;
  shipmentTypeId: number | string;
  name: string;
  phone?: string | null;
  company?: string;
  id?: number | null;
  address: string;
  local: string;
  postalCode: string;
  city?: number | null;
  state: number | string;
  country: number | string;
  door?: string | null;
  floor?: string | null;
  mainAddress?: number | null;
  latitude?: string;
  longitude?: string;
  allowSelect?: boolean;
  selected?: boolean;
  allowRemove?: boolean;
  allowEdit?: boolean;
  saved?: boolean;
}

/**
 * PointInfo - pickup point (from OrderService.getPoints)
 */
export interface PointInfo {
  _explicitType?: 'remote.order.PointInfo';
  id: number | string;
  name: string;
  email?: string;
  phone?: string;
  mPhone?: string;
  address: PointShipmentAddress;
  hours?: any[];
  selected?: boolean;
}

/**
 * StoreShipmentAddress - address format for stores
 */
export interface StoreShipmentAddress {
  _explicitType?: 'remote.common.StoreShipmentAddress';
  storeId?: number;
  storeName?: string;
  company?: string;
  name?: string;
  phone?: string | null;
  address?: string;
  floor?: string | null;
  door?: string | null;
  city?: number;
  country?: number;
  state?: number;
  postalCode?: string;
  local?: string;
  allowSelect?: boolean;
  selected?: boolean;
  allowRemove?: boolean;
  allowEdit?: boolean;
  mainAddress?: number;
  saved?: boolean;
  id?: number;
  mPhone?: string;
}

/**
 * StoreInfo - store location (from OrderService.getStores)
 */
export interface StoreInfo {
  _explicitType?: 'remote.order.StoreInfo';
  id: number | string;
  name: string;
  email: string;
  phone: string;
  image?: string;
  address: StoreShipmentAddress;
  mPhone?: string;
  hours?: any[];
  latitude?: string;
  longitude?: string;
  open1?: string;
  open2?: string;
  close1?: string;
  close2?: string;
  selected?: boolean;
}

/**
 * VoucherInfo - discount voucher (dreambooks-shaped wire contract).
 *
 * Applied vouchers on `order.vouchers[]` (add-to-cart / apply-discounts /
 * shipments / placed-order reads) carry the apply-time fields; the
 * validate-voucher per-voucher list carries the campaign fields + `valid`.
 * Read `statusKey` (stable string) in preference to the legacy `status` int.
 * The old `shipmentValueUsed` / `utilizations` fields were renamed on the wire
 * to `shippingValue` / `numberOfUses`.
 */
export interface VoucherInfo {
  voucherId: string;
  status: number;
  /** Stable status string — prefer over `status`. REDEEMED / REDEEMED_SHIPPING = applied. */
  statusKey: string;
  /** Total discount applied (product + applied shipping). */
  value: number;
  /** Shipping discount actually applied (apply-time, on order.vouchers). */
  shippingValue?: number;
  /** Products this voucher discounted (apply-time, on order.vouchers). */
  numberOfUses?: number;
  /** Campaign shipping-discount capacity — NOT the applied amount. */
  shipmentValue?: number;
  /** 0 = fixed, 1 = percentage. */
  shipmentDiscountType?: number;
  disableShippingRules?: boolean;
  error?: string | null;
  // --- validate-voucher per-voucher entries only ---
  valid?: boolean;
  discountType?: number;
  /** Present only when status == 15 (MIN_ORDER_VALUE). */
  minOrderValue?: number;
}

/**
 * A voucher counts as applied when its status is REDEEMED (1, applied to
 * products) or REDEEMED_SHIPPING (13, applied as shipping-only). Prefer the
 * stable `statusKey` string; fall back to the legacy int. Shared by the cart
 * and order-details so the applied-status rule lives in one place.
 */
export function isAppliedVoucher(v: any): boolean {
  if (v?.statusKey) return v.statusKey === 'REDEEMED' || v.statusKey === 'REDEEMED_SHIPPING';
  const s = parseInt(v?.status) || 0;
  return s === 1 || s === 13;
}

/**
 * Factory functions to create properly typed order objects
 */
export function createOrderInfo(partial?: Partial<OrderInfo>): OrderInfo {
  return {
    _explicitType: 'remote.order.OrderInfo',
    orderId: 0,
    code: null,
    orderDate: null,
    urgent: 0,
    status: 0,
    groups: [],
    vouchers: [],
    payment: null,
    currencyId: 0,
    totalValue: 0,
    baseValue: 0,
    vatValue: 0,
    showVat: 1,
    recargoValue: 0,
    vouchersTotalDiscount: 0,
    customerTotalDiscount: 0,
    totalDiscount: 0,
    balanceId: 0,
    discountedBalance: 0,
    orderTotals: null,
    shipmentType: null,
    shipmentMethod: null,
    shipmentAddress: null,
    shipmentTrackings: null,
    billingAddress: null,
    totalShippingPrice: 0,
    extras: [],
    warning: '00',
    issues: [],
    allowUrgent: 1,
    version: 2,
    estimatedDeliveryDate: null,
    ...partial
  };
}

/**
 * Generate a unique code similar to PHP's md5(uniqid(mt_rand(), true))
 */
function generateUniqueCode(): string {
  // Generate a random hex string that simulates md5 output (32 chars)
  const timestamp = Date.now().toString(16);
  const random = Math.random().toString(16).substring(2);
  const combined = timestamp + random + Math.random().toString(16).substring(2);
  // Pad to 32 characters
  return combined.substring(0, 32).padEnd(32, '0');
}

export function createOrderItemsGroupInfo(partial?: Partial<OrderItemsGroupInfo>): OrderItemsGroupInfo {
  return {
    _explicitType: 'remote.order.OrderItemsGroupInfo',
    id: 0,
    orderId: 0,
    code: partial?.code ?? generateUniqueCode(),  // Generate code if not provided
    status: OrderItemsGroupStatus.PENDING,
    products: [],
    pack: false,
    name: null,
    showcase: 0,
    showcaseEnabled: 1,
    showcaseStatus: 0,
    observations: '',
    price: 0,
    showPrices: false,
    extraValues: null,
    extraValueUnique: 0,
    backup: null,
    draftId: 0,
    unwantedProductTypes: [],
    estimatedDeliveryDate: null,
    ...partial
  };
}

export function createOrderItemInfo(partial?: Partial<OrderItemInfo>): OrderItemInfo {
  return {
    _explicitType: 'remote.order.OrderItemInfo',
    id: 0,
    processingStatus: 0,
    idName: null,
    code: null,
    refPHC: '',
    subCategoryName: null,
    productTypeId: 0,
    productSubTypeId: 0,
    productId: 0,
    productName: '',
    sectorId: 0,
    finishes: [],
    finishName: null,
    name: '',
    numPages: 0,
    numExtraPages: 0,
    margins: 0,
    qty: 1,
    qtyName: null,
    width: 0,
    height: 0,
    type: OrderItemType.NORMAL,
    projectInfo: null,
    files: [],
    showPrice: false,
    prices: null,
    price: 0,
    priceName: null,
    stock: null,
    color: 0x6D6E71,
    fontSize: 16,
    strokeColor: 0xcccccc,
    showVouchers: false,
    includeInOrder: true,
    ...partial
  };
}

export function createOrderItemFileInfo(partial?: Partial<OrderItemFileInfo>): OrderItemFileInfo {
  return {
    _explicitType: 'remote.order.OrderItemFileInfo',
    id: 0,
    name: null,
    serverName: null,
    serverId: 0,
    pathStructureId: 0,
    width: 0,
    height: 0,
    size: 0,
    md5: null,
    fileTypeId: 1,  // Default to JPEG (1) like AS3 ImageInfo
    qty: 1,
    thumbBytes: null,
    finishOptionId: 0,
    editorViewIndex: 0,
    isProcessedFile: false,
    processedFileUrl: null,
    deleted: false,
    ...partial
  };
}
